'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import {
  COMMANDS,
  filterCommands,
  type CommandDescriptor,
  type CommandRunUtils,
} from './commands';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  utils: CommandRunUtils;
}

// Cmd+K command palette. Modal Radix dialog hosting a search input
// + grouped command list. Arrow keys move the active row, Enter runs
// it, Escape closes. Style stays inside the editorial system: hairline
// dividers between groups, mono shortcut hints on the right, no
// chrome that screams "command palette".
export function CommandPalette({ open, onOpenChange, utils }: Props) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => filterCommands(query), [query]);

  // Reset query + selection each time the palette reopens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Radix autoFocus is unreliable across rerenders — focus the
      // input explicitly on the next tick.
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIndex]);

  function runCommand(cmd: CommandDescriptor) {
    cmd.run(utils);
    if (cmd.group === 'Go to') onOpenChange(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[activeIndex];
      if (cmd) runCommand(cmd);
    }
  }

  // Group rows for the rendered list; preserve the order COMMANDS
  // was declared in so the palette feels deliberate.
  const groupedView = useMemo(() => {
    const groups: Array<{ label: string; items: CommandDescriptor[] }> = [];
    for (const cmd of filtered) {
      const last = groups[groups.length - 1];
      if (last && last.label === cmd.group) {
        last.items.push(cmd);
      } else {
        groups.push({ label: cmd.group, items: [cmd] });
      }
    }
    return groups;
  }, [filtered]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in" />
        <Dialog.Content className="fixed left-1/2 top-[18%] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-hairline bg-surface/80 shadow-xl backdrop-blur-md focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <Dialog.Description className="sr-only">
            Search and run a command. Use arrow keys to move, Enter to select, Escape to close.
          </Dialog.Description>

          <div className="flex items-center gap-3 border-b border-hairline px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-subtle-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a command…"
              aria-label="Command search"
              className="flex-1 bg-transparent text-[15px] outline-none placeholder:text-subtle-foreground"
            />
            <Tooltip label="Close (Esc)">
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Close palette"
                  className="rounded-lg p-1.5 text-muted-foreground transition duration-fast ease-out-quint hover:bg-surface-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </Dialog.Close>
            </Tooltip>
          </div>

          <div className="max-h-[60vh] overflow-y-auto py-2">
            {filtered.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-subtle-foreground">
                No commands match “{query}”.
              </p>
            ) : (
              groupedView.map((group) => (
                <div key={group.label} className="py-1">
                  <p className="eyebrow px-4 pb-1.5 pt-1">{group.label}</p>
                  <ul>
                    {group.items.map((cmd) => {
                      const idx = filtered.indexOf(cmd);
                      const active = idx === activeIndex;
                      return (
                        <li key={cmd.id}>
                          <button
                            type="button"
                            onMouseEnter={() => setActiveIndex(idx)}
                            onClick={() => runCommand(cmd)}
                            aria-current={active ? 'true' : undefined}
                            className={cn(
                              'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition duration-fast ease-out-quint',
                              active ? 'bg-accent-surface text-foreground' : 'hover:bg-surface-muted/60',
                            )}
                          >
                            <cmd.icon
                              className={cn(
                                'h-4 w-4 shrink-0',
                                active ? 'text-accent' : 'text-muted-foreground',
                              )}
                            />
                            <span className="flex-1 truncate">{cmd.title}</span>
                            {cmd.shortcut && (
                              <ShortcutBadge keys={cmd.shortcut.split(' ')} muted={!active} />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-hairline bg-surface/60 px-4 py-2 text-[10px] uppercase tracking-eyebrow text-subtle-foreground">
            <p>
              <ShortcutBadge keys={['↑']} inline />{' '}
              <ShortcutBadge keys={['↓']} inline /> move ·{' '}
              <ShortcutBadge keys={['↵']} inline /> select ·{' '}
              <ShortcutBadge keys={['Esc']} inline /> close
            </p>
            <p>{COMMANDS.length} commands</p>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ShortcutBadgeProps {
  keys: string[];
  muted?: boolean;
  inline?: boolean;
}

// Small kbd-style badge for a shortcut. Two-key chords get a space
// between glyphs so "g s" reads as two separate presses.
function ShortcutBadge({ keys, muted, inline }: ShortcutBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono text-[10px]',
        muted ? 'text-subtle-foreground' : 'text-muted-foreground',
        !inline && 'ml-2',
      )}
    >
      {keys.map((k, i) => (
        <kbd
          key={i}
          className={cn(
            'rounded border border-border bg-background px-1.5 py-0.5 text-[10px] leading-none',
            inline && 'inline-block',
          )}
        >
          {k}
        </kbd>
      ))}
    </span>
  );
}
